import { Badge, Box, Button, Card, IconButton, Tooltip } from "@mui/material";
import { isEmpty, isNil, size } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FileUploadField from "../../../components/inputs/FileUploadField";
import Flx from "../../../components/layout/Flx";
import BasicModal from "../../../components/modals/BasicModal";
import Htag from "../../../components/typography/Htag";

import {
  CloseOutlined,
  DeleteOutlineOutlined,
  FileUploadOutlined,
} from "@mui/icons-material";
import { blue, grey } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import NumberInput from "../../../_src_shared/components/inputs/NumberInput";
import { columnTypesDate } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { columnTypesNumber } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesNumber";
import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import DateInput from "../../../components/inputs/DateInput";
import ToggleTabSwitcher from "../../../components/navigation/ToggleTabSwitcher";
import Txt from "../../../components/typography/Txt";
import {
  fileDocGroupAndTypes,
  fileDocGroups,
  getDocTypesByGroup,
  searchDocumentsAdvanced,
} from "../../../config/fileDocGroupAndTypes";
import { useLoanFilesHook } from "../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";

import FlxGrid from "../../../_src_shared/components/layout/FlxGrid";
import SearchInput from "../../../components/inputs/SearchInput";
import SelectFileAssignmentInput from "../../../components/inputs/SelectFileAssignmentInput";
import InputWrapper from "../../../components/inputs/shared/InputWrapper";
import LoanDocumentGroupIcon from "./LoanDocumentGroupIcon";

const LoanBulkFileUploader = ({ show, onClose }) => {
  const [filesList, setFilesList] = useState(null);
  const [docGroupFilter, setDocGroupFilter] = useState("All Categories");
  const [quickFilter, setQuickFilter] = useState(null);
  const [showTypes, setShowTypes] = useState(true);

  const onClearFiles = () => {
    setFilesList(null);
    setDocGroupFilter("All Categories");
  };

  const onModalClose = useCallback(() => {
    setFilesList(null);
    setDocGroupFilter("All Categories");
    onClose(false);
  }, []);

  const onFilesAdded = useCallback(
    (newFiles, docGroup, docType) => {
      // let updatedFiles = updateFilesMetadata(newFiles, docGroup, docType);
      if (isEmpty(filesList)) {
        setFilesList(newFiles);
        return;
      }

      setFilesList([...filesList, ...newFiles]);
    },
    [filesList]
  );

  // New function to update files in the filesList
  const updateFilesList = useCallback((updater) => {
    setFilesList((prevFiles) => {
      if (typeof updater === "function") {
        return updater(prevFiles);
      }
      return updater;
    });
  }, []);

  return (
    <ModalRoot show={show} onClose={onModalClose}>
      <SectionVerticalTabSwitcher
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        docGroupFilter={docGroupFilter}
        setDocGroupFilter={setDocGroupFilter}
        showTypes={showTypes}
        setShowTypes={setShowTypes}
      />
      <SectionFileUpload
        quickFilter={quickFilter}
        filesEmpty={isEmpty(filesList)}
        onFilesAdded={onFilesAdded}
        showTypes={showTypes}
        setShowTypes={setShowTypes}
        docGroupFilter={docGroupFilter}
      />

      <SectionFilesTable
        onClearFiles={onClearFiles}
        files={filesList}
        updateFiles={updateFilesList}
        onModalClose={onModalClose}
      />
    </ModalRoot>
  );
};

const SectionVerticalTabSwitcher = ({
  quickFilter,
  setQuickFilter,
  docGroupFilter,
  setDocGroupFilter,
  showTypes,
  setShowTypes,
}) => {
  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1,
        // background: grey[50],
        background: grey[100],
        p: 1,
        // borderRadius: 1,
        borderRight: `1px solid ${grey[300]}`,
      }}
    >
      <Flx
        fw
        center
        ac
        gap={2}
        sx={{
          ".file-upload-field": {
            flexGrow: 1,
            flexBasis: "470px",
            // p: 0.5,
            ".upload-text-label": { color: grey[800], fontSize: "11px" },
          },
        }}
      >
        <SearchInput
          value={quickFilter}
          onChange={setQuickFilter}
          placeholder={"Search document type..."}
          sx={{
            my: 1,
            mb: 2,
            ".MuiInputBase-root": {
              background: "#ffffff",
              borderRadius: "50px !important",
            },
            input: {
              borderRadius: "50px",
              fontSize: "11px",
            },
            // minWidth: "120px",
          }}
        />
        {/* <FormControlLabel
          label="Show Document Types"
          sx={{ flexShrink: 0, span: { fontSize: "12px" } }}
          control={
            <Checkbox
              size="small"
              checked={showTypes}
              onChange={(e) => setShowTypes(e.target.checked)}
            />
          }
        /> */}
      </Flx>
      <Flx
        column
        sx={{
          ".toggle-tab-switcher-root": {
            // background: grey[50],
            // border: `1px solid ${grey[200]}`,
            gap: 0,
          },
          ".Mui-selected": {
            backgroundColor: `${blue[50]} !important`,
          },
          ".MuiButtonBase-root": {
            fontSize: " 11px !important",
            // p: 0,
            py: "3px !important",
            px: "4px !important",
            // borderRadius: 0,
            // borderLeft: `1px solid ${grey[400]}`,
          },
        }}
      >
        <Txt bold fontSize={12} sx={{ p: "6px !important" }}>
          Categories
        </Txt>

        <ToggleTabSwitcher
          orientation="vertical"
          value={docGroupFilter}
          onChange={(v) => {
            if (v === "All Categories") {
              setDocGroupFilter(null);
            } else {
              setDocGroupFilter(v);
            }
          }}
          tabs={["All Categories", ...Object.keys(fileDocGroupAndTypes)]}
        />
      </Flx>
    </Box>
  );
};

const SectionFileUpload = ({ quickFilter, onFilesAdded, docGroupFilter }) => {
  const filteredGroupsAndTypes = useMemo(() => {
    // If a specific category is selected, only show that category
    if (docGroupFilter && docGroupFilter !== "All Categories") {
      // Check if the selected category matches the search filter
      if (isNil(quickFilter) || quickFilter === "" || quickFilter === " ") {
        return [docGroupFilter];
      }
      // Check if the selected category has any matching document types
      const searchResults = searchDocumentsAdvanced(quickFilter);
      if (searchResults[docGroupFilter]) {
        return [docGroupFilter];
      }
      return [];
    }

    // If "All Categories" is selected or no filter, show all matching categories
    if (isNil(quickFilter) || quickFilter === "" || quickFilter === " ") {
      return Object.keys(fileDocGroupAndTypes);
    }

    return Object.keys(searchDocumentsAdvanced(quickFilter));
  }, [quickFilter, docGroupFilter]);

  return (
    <Flx
      column
      g={1}
      sx={{
        flexGrow: 1,
        background: grey[100],
        overflowY: "auto",
        height: "100%",
        flexBasis: "680px",
        p: 2,
        borderRadius: 1,
      }}
    >
      <Flx
        column
        // wrap
        fw
        // width="380px"
        gap={2}
        sx={{
          flexGrow: 1,
          // mt: 1,
        }}
      >
        <Txt>
          Drag and dropping your loan documents into the appropriate categories.
        </Txt>
        {/* <Txt bold>
          Organize your loan documents by dragging and dropping them into the
          appropriate categories.
        </Txt> */}
        {isEmpty(filteredGroupsAndTypes) && (
          <Txt secondary>No results found for the term "{quickFilter}".</Txt>
        )}
        {filteredGroupsAndTypes.map((group) => {
          return (
            <DocGroupUploadCard
              docGroup={group}
              docType={null}
              quickFilter={quickFilter}
              onFilesAdded={onFilesAdded}
            />
          );
        })}
      </Flx>
    </Flx>
  );
};

const SectionFilesTable = ({
  files,
  onClearFiles,
  onModalClose,
  updateFiles,
}) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const ref = useRef();

  const [selectedRows, setSelectedRows] = useState(null);

  const { loading, uploadLoanDocs } = useLoanFilesHook();
  const dispatch = useDispatch();

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Filename",
        field: "file_display_name",
        flex: 1,
        minWidth: 250,
        cellEditor: "agTextCellEditor",
        editable: true,
        checkbox: true,
      },
      {
        headerName: "Document Group",
        field: "docGroup",
        editable: true,
        width: 160,
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true, // Enable popup mode for the dropdown
        cellEditorParams: {
          values: fileDocGroups || [], // Replace with your actual values
        },
      },
      {
        headerName: "Document Type",
        field: "docType",
        editable: true,
        width: 160,
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true, // Enable popup mode for the dropdown
        cellEditorParams: (params) => {
          return {
            values: getDocTypesByGroup(params.data.docGroup || "Appraisal"),
          };
        },
      },
      {
        headerName: "Report Effective Date",
        field: "reportEffectiveDate",
        editable: true,
        type: "date",
        width: 120,
        wrapHeaderText: true, // Wrap Column Group Header
        autoHeaderHeight: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Report Guideline Expiration Days",
        field: "reportGuidelineExpirationDays",
        editable: true,
        type: "number",
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true, // Wrap Column Group Header
        autoHeaderHeight: true,
        width: 140,
      },
    ],
    []
  );

  const onClearFilesClick = useCallback(
    (props) => {
      setSelectedRows(null);
      onClearFiles();
    },
    [onClearFiles]
  );

  const handleUploadFiles = () => {
    uploadLoanDocs({
      loanId: loanDrilldown?._id,
      files: files,
      onSuccessMsg:
        size(files) > 1
          ? `${size(files)} files uploaded successfully`
          : "File uploaded successfully",
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        // dispatch(loanDrilldownSet(data));
        onClearFilesClick();
        onModalClose();
      },
    });
  };

  const handleRowSelectionChange = useCallback((params) => {
    const selected = params.api.getSelectedRows();
    setSelectedRows(selected);
  }, []);

  // Handle deleting the selected rows
  const handleDeleteSelectedRows = useCallback(() => {
    if (!selectedRows || selectedRows.length === 0) return;

    // pull out the IDs of whatever is selected
    const selectedIds = selectedRows.map((r) => r.id);

    // Update the parent's filesList by filtering out selected rows
    updateFiles((prevFiles) =>
      prevFiles.filter((row) => !selectedIds.includes(row.id))
    );

    // also clear AG-Grid's built-in selection
    if (ref.current && ref.current.api) {
      ref.current.api.deselectAll();
    }

    // mirror that in React state
    setSelectedRows([]);
  }, [selectedRows, updateFiles]);

  const handleDeselectAllRows = useCallback(() => {
    // ask AG-Grid to drop its selection
    if (ref.current && ref.current.api) {
      ref.current.api.deselectAll();
    }
    // mirror that in React state
    setSelectedRows([]);
  }, []);

  const handleCellValueChanged = useCallback(
    (params) => {
      const { colDef, data, newValue } = params;

      updateFiles((prevFiles) => {
        return prevFiles.map((item) => {
          if (item.id === data.id) {
            const updatedItem = { ...item };

            // If the user changes the filename
            if (colDef.field === "file_display_name") {
              // Pull the real extension from `data.name`
              const originalExtension = item.name
                .split(".")
                .pop()
                .toLowerCase();
              // Remove any extension from the user's typed value
              const newValueWithoutExt = newValue.replace(/\.[^/.]+$/, "");
              // Re-append original extension
              updatedItem.file_display_name = `${newValueWithoutExt}.${originalExtension}`;
            }

            // If the user changes docGroup
            if (colDef.field === "docGroup") {
              updatedItem.docGroup = newValue;
              // If there's already a docType, remove it
              if (updatedItem.docType) {
                updatedItem.docType = null;
              }
            }

            // If the user changes docType
            if (colDef.field === "docType") {
              updatedItem.docType = newValue;
            }

            // If the user changes date values, ensure they're stored as ISO strings
            if (colDef.field === "reportEffectiveDate") {
              // Ensure the value is an ISO string
              if (newValue instanceof Date) {
                updatedItem.reportEffectiveDate = newValue.toISOString();
              } else if (typeof newValue === "string" && newValue) {
                // If it's already a string but not an ISO string, convert it
                if (!newValue.includes("T") || !newValue.endsWith("Z")) {
                  const dateObj = new Date(newValue);
                  if (!isNaN(dateObj.getTime())) {
                    updatedItem.reportEffectiveDate = dateObj.toISOString();
                  }
                }
              } else {
                updatedItem.reportEffectiveDate = newValue;
              }
            }

            // If the user changes expiration days
            if (colDef.field === "reportGuidelineExpirationDays") {
              updatedItem.reportGuidelineExpirationDays = newValue;
            }

            return updatedItem;
          }
          return item;
        });
      });

      setTimeout(() => {
        if (ref.current && ref.current.api) {
          ref.current.api.refreshCells();
        }
      }, 0);
    },
    [updateFiles]
  );

  const handleMetadataUpdate = useCallback(
    ({
      newDocGroup,
      newDocType,
      newEffectiveDate,
      newGuidelineExpirationDays,
    }) => {
      if (!selectedRows || selectedRows.length === 0) return;
      const selectedRowIds = selectedRows.map((row) => row.id);

      updateFiles((prevFiles) => {
        return prevFiles.map((row) => {
          if (!selectedRowIds.includes(row.id)) return row;
          const updatedRow = { ...row };

          // 1) Group change: always update; clear type only if no new type coming
          if (newDocGroup !== undefined) {
            updatedRow.docGroup = newDocGroup;
            if (newDocType === undefined) {
              updatedRow.docType = null;
            }
          }

          // 2) Type change: only update if provided
          if (newDocType !== undefined) {
            updatedRow.docType = newDocType;
          }

          // 3) Date change
          if (newEffectiveDate !== undefined) {
            updatedRow.reportEffectiveDate =
              newEffectiveDate instanceof Date
                ? newEffectiveDate.toISOString()
                : newEffectiveDate;
          }

          // 4) Expiration-days change
          if (newGuidelineExpirationDays !== undefined) {
            updatedRow.reportGuidelineExpirationDays =
              newGuidelineExpirationDays;
          }

          return updatedRow;
        });
      });

      // force a grid refresh
      setTimeout(() => {
        if (ref.current && ref.current.api) {
          ref.current.api.refreshCells();
        }
      }, 0);
    },
    [selectedRows, updateFiles]
  );

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesDate(),
      ...columnTypesNumber(),
    };
  }, []);

  if (isEmpty(files)) {
    return null;
  }
  return (
    <Flx
      // flexGrow={1}
      flexGrow={0}
      column
      gap={0}
      sx={{
        mt: 0,
        //
        flexBasis: "880px",
        // flexBasis: "800px",
        borderLeft: `1px solid ${grey[200]}`,
      }}
    >
      {isEmpty(selectedRows) ? (
        <Box
          sx={{
            p: 2,
            background: grey[50],
            // pb: 0,
            // borderTop: `1px solid ${grey[200]}`,
            borderBottom: `1px solid ${grey[200]}`,
          }}
        >
          <Htag
            h4
            sx={{
              fontWeight: 600,
            }}
          >
            Added Files ({size(files)})
          </Htag>
        </Box>
      ) : (
        <BulkActionsToolbar
          files={files}
          selectedRows={selectedRows}
          onDeleteSelectedRows={handleDeleteSelectedRows}
          onMetadataUpdate={handleMetadataUpdate}
          onDeselectAllRows={handleDeselectAllRows}
        />
      )}
      <Flx column sx={{ flexGrow: 1, overflowY: "auto", position: "relative" }}>
        <AgGridTableWrapper height="100%" simpleTable size={4}>
          <AgGridReact
            ref={ref}
            getRowId={(params) => params.data.id}
            rowData={files}
            columnDefs={columnDefs}
            singleClickEdit
            columnTypes={columnTypes}
            onSelectionChanged={handleRowSelectionChange}
            domLayout={"autoHeight"}
            onCellValueChanged={handleCellValueChanged}
            stopEditingWhenCellsLoseFocus
            rowSelection={{
              mode: "multiRow",
            }}
          />
        </AgGridTableWrapper>
      </Flx>

      <Flx
        fw
        jb
        ac
        sx={{ borderTop: `1px solid ${grey[200]}`, p: 2, background: grey[50] }}
      >
        <Flx end flexGrow={1} gap={1}>
          <Button onClick={onClearFilesClick} variant="outlined" size="large">
            Clear Files
          </Button>
          <Button size="large" onClick={handleUploadFiles} loading={loading}>
            Upload Files
          </Button>
        </Flx>
      </Flx>
    </Flx>
  );
};

/**
 * SHARED COMPONENTS
 */
const DocGroupUploadCard = ({
  onFilesAdded,
  docGroup,
  docType,
  showTypes,
  quickFilter,
}) => {
  const cardWidth = "100%";
  const fileUploadWidth = "200px";
  const docGroupTypes = useMemo(() => {
    const allTypes = getDocTypesByGroup(docGroup);

    // If no quick filter, return all types
    if (isNil(quickFilter) || quickFilter === "" || quickFilter === " ") {
      return allTypes;
    }

    // Filter types based on quick filter
    const normalizedFilter = quickFilter.toLowerCase().trim();
    const filterWords = normalizedFilter.split(/\s+/);

    return allTypes.filter((type) => {
      const normalizedType = type.toLowerCase();
      return filterWords.some((word) => normalizedType.includes(word));
    });
  }, [quickFilter, docGroup]);

  const shouldShowUnassigned = useMemo(() => {
    // If no quick filter, always show unassigned
    if (isNil(quickFilter) || quickFilter === "" || quickFilter === " ") {
      return true;
    }

    // Check if "unassigned" matches the filter
    const normalizedFilter = quickFilter.toLowerCase().trim();
    const filterWords = normalizedFilter.split(/\s+/);

    return filterWords.some((word) => "unassigned".includes(word));
  }, [quickFilter]);

  // Don't render the card if no types match and unassigned doesn't match
  if (!shouldShowUnassigned && docGroupTypes.length === 0) {
    return null;
  }

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        // flexBasis: cardWidth,
        // flexGrow: 1,
        flexShrink: 1,
        height: "max-content",
        background: "#ffffff",
        p: 1,
        gap: 0.5,
        ".file-upload-field": {
          flexGrow: 1,
          // p:1,
          flexBasis: fileUploadWidth,
          ".upload-text-label": { color: grey[900], fontSize: "10px", p: 1 },
        },
      }}
    >
      {/* CARD HEADER */}
      <Flx column gap={0.5}>
        <Flx ac gap={0.5} sx={{ pt: 0.4, pb: 0.8, svg: { fontSize: "16px" } }}>
          <LoanDocumentGroupIcon styled docGroup={docGroup} size={20} />
          <Txt color={grey[600]} fontSize="12px" sx={{ fontWeight: 600 }}>
            {docGroup}
          </Txt>
        </Flx>
      </Flx>

      {/* LIST OF DOC TYPES */}
      {/* <FlxGrid width="140px" wrap g={0.5}> */}
      <FlxGrid width="210px" wrap g={0.5}>
        {/* Show filtered document types */}
        {docGroupTypes.map((type) => {
          return (
            <DocTypeFileUploadBox
              key={type}
              docGroup={docGroup}
              docType={type}
              onFilesAdded={onFilesAdded}
            />
          );
        })}

        {/* Only show unassigned if it matches the filter */}
        {shouldShowUnassigned && (
          <FileUploadField
            onFilesAdded={(f) => onFilesAdded(f, docGroup, null)}
            suppressGrid
            hideUploadButton
            docGroup={docGroup}
            borderSize={1}
            background={"#ffffff"}
            suppressFileList
            minHeight={"72px"}
            // minHeight={"64px"}
            uploadText={"Unassigned"}
            suppressUploadButton
          />
        )}
      </FlxGrid>
    </Card>
  );
};

const DocTypeFileUploadBox = ({ onFilesAdded, docGroup, docType }) => {
  return (
    <FileUploadField
      onFilesAdded={(f) => onFilesAdded(f, docGroup, docType)}
      suppressGrid
      hideUploadButton
      docType={docType}
      borderSize={1}
      docGroup={docGroup}
      background={"#ffffff"}
      suppressFileList
      minHeight={"72px"}
      // minHeight={"64px"}
      uploadText={docType}
      suppressUploadButton
      // sx={{
      //   flexGrow: 1,
      //   flexBasis: "120px",
      //   p: 0.5,
      // }}
    />
  );
};

const BulkActionsToolbar = ({
  files,
  selectedRows,
  onDeselectAllRows,
  onDeleteSelectedRows,
  onMetadataUpdate,
}) => {
  const [bulkDocGroup, setBulkDocGroup] = useState(null);
  const [bulkDocType, setBulkDocType] = useState(null);
  const [bulkEffectiveDate, setBulkEffectiveDate] = useState("");
  const [bulkNumExpirationDays, setBulkNumExpirationDays] = useState(null);

  const onGroupChange = ({ docGroup, docType }) => {
    setBulkDocGroup(docGroup);
    setBulkDocType(docType);
    onMetadataUpdate({ newDocGroup: docGroup, newDocType: docType });
  };

  const onEffectiveDateChange = (date) => {
    setBulkNumExpirationDays(date);
    onMetadataUpdate({ newEffectiveDate: date });
  };

  const onNumExpirationDaysChange = (num) => {
    setBulkNumExpirationDays(num);
    onMetadataUpdate({ newGuidelineExpirationDays: num });
  };

  useEffect(() => {
    if (isEmpty(selectedRows)) {
      setBulkDocGroup(null);
      setBulkDocType(null);
      setBulkEffectiveDate(null);
    }
  }, [selectedRows]);

  if (isEmpty(selectedRows)) {
    return null;
  }
  return (
    <Flx
      fw
      // jb
      // ac
      column
      sx={{
        background: blue[50],
        p: 2,
        pt: 1,
        zIndex: "1111",
        ".MuiSelect-select, .MuiInputBase-root, .txt": {
          fontSize: "12px",
        },
      }}
    >
      <Flx fw jb ac gap={1} sx={{ mb: 0.5 }}>
        {/* <Txt sx={{ lineHeight: "normal" }}>
          {`Selected (${size(selectedRows)})`}
        </Txt> */}
        <Htag
          h4
          sx={{
            fontWeight: 600,
          }}
        >
          {`Selected (${size(selectedRows)} of ${size(files)})`}
        </Htag>
        <Flx ac gap={1}>
          <Tooltip
            title={
              size(selectedRows) === 1
                ? "Delete File"
                : `Delete ${size(selectedRows)} Files`
            }
            disableInteractive
          >
            <Badge
              color="error"
              overlap="circular"
              badgeContent={size(selectedRows)}
              sx={{
                ".MuiBadge-badge": {
                  minWidth: "16px",
                  height: "16px",
                  fontSize: "10px",
                },
              }}
            >
              <IconButton
                onClick={onDeleteSelectedRows}
                color="error"
                variant="outlined"
                // sx={{ p: 1 }}
                size="small"
              >
                <DeleteOutlineOutlined
                  sx={{ fontSize: "18px" }}
                  className="thin-8"
                />
              </IconButton>
            </Badge>
          </Tooltip>
          <Tooltip
            placement="top-start"
            title={`Deselect All`}
            disableInteractive
          >
            <IconButton
              onClick={onDeselectAllRows}
              variant="outlined"
              size="small"
            >
              <CloseOutlined sx={{ fontSize: "18px" }} className="thin-8" />
            </IconButton>
          </Tooltip>
        </Flx>
      </Flx>
      <Flx fw gap={1.5}>
        <InputWrapper fullWidth label="Document Assignment">
          <SelectFileAssignmentInput
            placeholder="Document Group"
            docGroup={bulkDocGroup}
            docType={bulkDocType}
            onChange={onGroupChange}
            sx={{ width: "100%", minWidth: "120px" }}
          />
        </InputWrapper>
        <InputWrapper label="Report Effective Date">
          <DateInput
            fullWidth={false}
            value={bulkEffectiveDate}
            placeholder="Effective Date"
            onChange={onEffectiveDateChange}
            sx={{ width: "160px", minWidth: "140px" }}
          />
        </InputWrapper>

        <InputWrapper label="Report Expiration Days">
          <NumberInput
            value={bulkNumExpirationDays}
            placeholder="Num. of Days"
            onChange={onNumExpirationDaysChange}
            sx={{ width: "160px", minWidth: "140px" }}
          />
        </InputWrapper>
      </Flx>
    </Flx>
  );
};

const ModalRoot = ({ show, onClose, children }) => {
  return (
    <BasicModal
      allowFullScreen
      disableAutoMobile={false}
      icon={<FileUploadOutlined />}
      title={"Upload Loan Documents"}
      show={show}
      // description={
      //   "Organize your loan documents by dragging and dropping them into the appropriate categories."
      // }
      // description="Drag and drop loan documents into their respective categories."
      // description="Drag and drop files or select files to upload them to the loan."
      defaultFullScreen
      onClose={onClose}
      maxWidth={"xl"}
      bodySx={{
        p: 0,
        pt: 0,
        ".MuiSelect-select": {
          fontSize: "12px",
        },
        ".ag-popup": {
          position: "absolute !important",
        },
        ".ag-rich-select": {
          position: "absolute !important",
        },
        ".ag-popup-child": {
          position: "absolute !important",
        },
      }}
    >
      <Flx
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          overflow: "hidden",
          height: "100%",
        }}
      >
        {children}
      </Flx>
    </BasicModal>
  );
};
export default LoanBulkFileUploader;
